import {
  Button,
  Checkbox,
  Message,
  SelectPicker,
  Table,
  Uploader,
  useToaster,
} from 'rsuite';
import React, { useEffect } from 'react';

import * as groupsApi from '../../api/groups';
import * as coursesApi from '../../api/courses';
import CheckIcon from '@rsuite/icons/Check';
import DeleteIcon from '@rsuite/icons/Trash';
import FileUploadIcon from '@rsuite/icons/FileUpload';
import { API_DOMAIN } from '../../config';
import { UploaderInstance } from 'rsuite/cjs/Uploader/Uploader';
import RequestsAndStudentsSearcher from '../../common/RequestsAndStudentsSearcher';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useConfirm } from '../../common/useConfirm';
import {
  FormField,
  FormGrid,
  FormModal,
  FormSection,
} from '../../common/FormModal';
import { useFormErrors } from '../../common/useFormErrors';
import { useCsrfHeaders } from '../../common/useCsrfHeaders';

const { Column, HeaderCell, Cell } = Table;
type Props = {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  coursesData: { value: string; label: string }[];
  typesData: { value: number; label: string }[];
  callback: () => void;
};

export default function CreateGroupModal({
  open,
  setOpen,
  coursesData,
  typesData,
  callback,
}: Props) {
  const [confirm, ConfirmDialog] = useConfirm();
  const [fileList, setFileList] = React.useState<any>([]);
  const uploader = React.useRef<UploaderInstance>();
  let cellClicked = false;
  const toaster = useToaster();
  const { errorSummary, setApiError, clearErrors, setErrorSummary } =
    useFormErrors({
      fallbackMessage: 'Не вдалося створити групу. Спробуйте пізніше',
      resetOn: [open],
    });
  const csrfHeaders = useCsrfHeaders();
  const [studentsList, setStudentsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [requestsList, setRequestsList] = React.useState<
    {
      id: any;
      email: any;
      firstName: string;
      secondName: string;
    }[]
  >([]);
  const [selectedCourseId, setSelectedCourseId] = React.useState<string>('');
  const [selectedType, setSelectedType] = React.useState<number | null>();
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [addToQueueToggle, setAddToQueueToggle] =
    React.useState<boolean>(false);
  const [queueLoading, setQueueLoading] = React.useState<boolean>(false);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
  const [queue, setQueue] = React.useState<any[]>([]);
  const hasSelectedRows = selectedRows.length > 0;
  const hasQueueSelections = requestsList.length > 0 || studentsList.length > 0;
  const selection = ({ id }: { id: string }) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(r => r !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  useEffect(() => {
    if (!selectedCourseId) {
      return;
    }
    getQueue()
      .catch(console.error)
      .then(() => setQueueLoading(false));
  }, [selectedCourseId]);

  const getQueue = async () => {
    setQueueLoading(true);
    const students = await coursesApi
      .getQueue(selectedCourseId, 'createdAt', sortOrder.toUpperCase())
      .catch(console.error);
    if (students) {
      setQueue(students.queue);
      // setFoundRequests(foundRequests.filter(r => !students.queue.find((q: any) => q.requestId === r.id)));
    }
    setQueueLoading(false);
  };

  const deleteQueueEntries = async (studentIds: string[]) => {
    if (!selectedCourseId) {
      return;
    }
    clearErrors();
    try {
      await coursesApi.removeFromQueue(selectedCourseId, studentIds);
      toaster.push(
        <Message type="success" showIcon closable>
          Студент{studentIds.length > 1 ? 'ів' : 'а'} видалено з черги
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      getQueue().catch(console.error);
    } catch (err: any) {
      console.error(err);
      const errorMessage = setApiError(
        err,
        'Не вдалося оновити чергу. Спробуйте пізніше'
      )[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  const addQueueEntries = async (
    requestIds: string[],
    studentIds: string[]
  ) => {
    if (!selectedCourseId) {
      return;
    }
    clearErrors();
    try {
      await coursesApi.addToQueue(selectedCourseId, requestIds, studentIds);
      toaster.push(
        <Message type="success" showIcon closable>
          Чергу оновлено
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      setRequestsList([]);
      setStudentsList([]);
      getQueue().catch(console.error);
    } catch (err: any) {
      console.error(err);
      const errorMessage = setApiError(
        err,
        'Не вдалося додати студентів. Спробуйте пізніше'
      )[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={open}
        onClose={() => setOpen(false)}
        title="Створити групу"
        errorSummary={errorSummary}
        footer={
          <>
            <Button
              onClick={async () => {
                clearErrors();
                if (
                  !selectedCourseId ||
                  (!selectedType && selectedType !== 0)
                ) {
                  setErrorSummary(['Оберіть курс та тип групи']);
                  toaster.push(
                    <Message type="error" showIcon closable>
                      Оберіть курс та тип групи
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  return;
                }
                if (!selectedRows.length) {
                  setErrorSummary(['Оберіть студентів для групи']);
                  toaster.push(
                    <Message type="error" showIcon closable>
                      Оберіть студентів для групи
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  return;
                }
                try {
                  await groupsApi.create(
                    selectedCourseId,
                    selectedType,
                    selectedRows
                  );
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Групу створено
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  setOpen(false);
                  setSelectedType(null);
                  setSelectedRows([]);
                  setSelectedCourseId('');
                  setQueue([]);
                  callback();
                } catch (e: any) {
                  console.error(e);
                  const errorMessage = setApiError(e)[0];
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }}
              appearance="primary"
            >
              Створити
            </Button>
            <Button onClick={() => setOpen(false)} appearance="subtle">
              Скасувати
            </Button>
          </>
        }
      >
        <FormSection title="Параметри групи">
          <FormGrid>
            <FormField label="Оберіть курс" id="create-group-course" required>
              <SelectPicker
                data={coursesData}
                value={selectedCourseId}
                placeholder="Оберіть курс"
                block
                cleanable={false}
                onSelect={value => {
                  setSelectedRows([]);
                  setSelectedCourseId(value);
                }}
              />
            </FormField>
            <FormField
              label="Оберіть тип групи"
              id="create-group-type"
              required
            >
              <SelectPicker
                data={typesData}
                value={selectedType}
                placeholder="Оберіть тип групи"
                block
                searchable={false}
                onSelect={value => {
                  setSelectedType(value);
                }}
              />
            </FormField>
          </FormGrid>
          <FormField label="Імпорт файлу групи" id="create-group-import">
            <div className="form-modal__card form-modal__card--muted">
              <div className="form-modal__inline">
                <Uploader
                  id={'file-uploader'}
                  multiple={false}
                  draggable
                  listType="text"
                  ref={uploader}
                  fileList={fileList}
                  withCredentials={true}
                  onChange={setFileList}
                  onSuccess={() => {
                    toaster.push(
                      <Message type="success" showIcon closable>
                        Групу імпортовано
                      </Message>,
                      {
                        placement: 'topEnd',
                        duration: 1500,
                      }
                    );
                    setFileList([]);
                    setOpen(false);
                    setSelectedType(null);
                    setSelectedRows([]);
                    setSelectedCourseId('');
                    setQueue([]);
                    callback();
                  }}
                  onError={(status, file, event, xhr) => {
                    clearErrors();
                    let message = 'Помилка завантаження файлу';
                    if (xhr.responseText) {
                      try {
                        const body = JSON.parse(xhr.responseText);
                        message = body?.message || message;
                      } catch {
                        // backend returned non-JSON (e.g. nginx HTML error page)
                      }
                    }
                    setErrorSummary([message]);
                    toaster.push(
                      <Message type="error" showIcon closable>
                        {message}
                      </Message>,
                      { placement: 'topEnd', duration: 1500 }
                    );
                  }}
                  action={`${API_DOMAIN}/api/groups/import`}
                  name={'group'}
                  autoUpload={false}
                  headers={csrfHeaders}
                >
                  <Button appearance="ghost">
                    <FileUploadIcon /> Завантажити файл
                  </Button>
                </Uploader>
                {fileList.length > 0 ? (
                  <Button
                    appearance="primary"
                    size="sm"
                    disabled={!fileList.length}
                    onClick={() => {
                      if (!uploader?.current) {
                        return;
                      }
                      uploader.current.start();
                    }}
                  >
                    Імпорт
                  </Button>
                ) : null}
              </div>
              <span className="form-modal__hint">
                Додайте файл із чергою студентів або використовуйте пошук нижче.
              </span>
            </div>
          </FormField>
        </FormSection>

        <FormSection title="Черга студентів">
          <FormGrid>
            <FormField label="Додати студентів у чергу">
              <div className="form-modal__inline">
                <Button
                  appearance={addToQueueToggle ? 'primary' : 'ghost'}
                  onClick={() => {
                    setAddToQueueToggle(!addToQueueToggle);
                  }}
                >
                  {addToQueueToggle ? 'Сховати пошук' : 'Додати в чергу'}
                </Button>
                <span className="form-modal__hint">
                  Знайдіть заявки або студентів і додайте їх до курсу
                </span>
              </div>
            </FormField>
            <FormField label="Дії з вибраними">
              <div className="form-modal__inline form-modal__inline--end">
                <Button
                  appearance="ghost"
                  disabled={!hasSelectedRows}
                  onClick={async () => {
                    if (!hasSelectedRows) {
                      return;
                    }
                    const result = await confirm({
                      message: `Ви впевнені, що хочете видалити з черги ${selectedRows.length} студентів?`,
                      destructive: true,
                      confirmText: 'Видалити',
                    });
                    if (!result) {
                      return;
                    }
                    await deleteQueueEntries(selectedRows);
                  }}
                >
                  Прибрати з черги
                </Button>
                <span className="form-modal__hint">
                  {hasSelectedRows
                    ? `Вибрано: ${selectedRows.length}`
                    : 'Позначте студентів у таблиці'}
                </span>
              </div>
            </FormField>
          </FormGrid>

          {addToQueueToggle ? (
            <div className="form-modal__card form-modal__card--flush">
              <div className="form-modal__search-row">
                <RequestsAndStudentsSearcher
                  requestsList={requestsList}
                  studentsList={studentsList}
                  setRequestsList={setRequestsList}
                  setStudentsList={setStudentsList}
                  requestsFilter={(requests: any[]) =>
                    requests.filter(
                      r => !queue.find(qS => qS.requestId === r.id)
                    )
                  }
                  studentsFilter={(students: any[]) =>
                    students.filter(s => !queue.find(qS => qS.id === s.id))
                  }
                />
                <Button
                  appearance="primary"
                  disabled={!selectedCourseId || !hasQueueSelections}
                  onClick={async () => {
                    await addQueueEntries(
                      requestsList.map(r => r.id),
                      studentsList.map(s => s.id)
                    );
                  }}
                >
                  <CheckIcon /> Додати
                </Button>
              </div>
            </div>
          ) : null}

          <div className="form-modal__table">
            <Table
              loading={queueLoading}
              autoHeight={true}
              renderLoading={renderTableLoading}
              renderEmpty={renderTableEmpty}
              onRowClick={rowData => {
                if (cellClicked) {
                  cellClicked = false;
                  return;
                }
                selection({ id: rowData.id });
              }}
              sortColumn={'createdAt'}
              sortType={sortOrder}
              onSortColumn={(sortColumn, sortType) => {
                setSortOrder(sortType || 'asc');
                getQueue().catch(console.error);
              }}
              data={queue}
            >
              <Column width={64} resizable={true}>
                <HeaderCell>
                  <Button
                    appearance="link"
                    onClick={() => {
                      if (selectedRows.length === queue.length) {
                        setSelectedRows([]);
                      } else {
                        setSelectedRows(queue.map(q => q.id));
                      }
                    }}
                  >
                    <CheckIcon />
                  </Button>
                </HeaderCell>
                <Cell style={{ display: 'flex', alignItems: 'center' }}>
                  {rowData => (
                    <Checkbox
                      checked={selectedRows.includes(rowData.id)}
                      onCheckboxClick={() => {
                        selection({ id: rowData.id });
                      }}
                    />
                  )}
                </Cell>
              </Column>
              <Column width={200} resizable={true}>
                <HeaderCell>Пошта</HeaderCell>
                <Cell dataKey="email" />
              </Column>
              <Column width={180} resizable={true}>
                <HeaderCell>Тип</HeaderCell>
                <Cell dataKey="typeName" />
              </Column>
              <Column width={180} resizable={true}>
                <HeaderCell>Ім'я</HeaderCell>
                <Cell dataKey="firstName" />
              </Column>
              <Column width={180} resizable={true}>
                <HeaderCell>Прізвище</HeaderCell>
                <Cell dataKey="secondName" />
              </Column>
              <Column width={180} sortable={true} resizable={true}>
                <HeaderCell>Очікує з</HeaderCell>
                <Cell dataKey="createdAt">
                  {rowData =>
                    new Date(rowData.queueEntryCreatedAt).toLocaleString()
                  }
                </Cell>
              </Column>
              <Column width={80} resizable={true}>
                <HeaderCell>Дії</HeaderCell>
                <Cell>
                  {rowData => (
                    <>
                      <Button
                        appearance="subtle"
                        onClick={async () => {
                          cellClicked = true;
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити з черги ${rowData.email} (${rowData.firstName} ${rowData.secondName})?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          await deleteQueueEntries([rowData.id]);
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
          {hasSelectedRows ? (
            <p className="form-modal__hint">Виділено: {selectedRows.length}</p>
          ) : null}
        </FormSection>
      </FormModal>
    </>
  );
}
